import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopy,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tooltip,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, MicrochipIcon, SyncAltIcon, TrashIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useGpuServer } from '../../../hooks/useGpuServer';
import type { GpuServer } from '../../../types/models/GpuServer';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatPrice, formatUnitPrice, trimDecimalZeros } from '../../../utils/money';
import DeleteGpuServerModal from './DeleteGpuServerModal';
import GpuServerImpersonationAlert from './GpuServerImpersonationAlert';
import GpuServerStatusLabel from './GpuServerStatusLabel';
import { GPU_SERVER_SSH_USER } from './gpuServerConstants';
import { diskSentence, gpuServerStatusCopy, isDeletable } from './gpuServerStatus';

type StatusAlertVariant = 'success' | 'danger' | 'warning' | 'info' | 'custom';

// The status copy's alert tone. `unpaid` is a deletion in progress, never a mere warning.
const STATUS_ALERT_VARIANT: Record<string, StatusAlertVariant> = {
  starting: 'info',
  running: 'success',
  unpaid: 'danger',
  deleting: 'warning',
  deleted: 'custom',
  failed: 'danger',
  unavailable: 'danger',
};

/** The route param as a server id: a positive integer made of digits only, else `null` (not found). */
export function parseGpuServerId(raw: string | undefined): number | null {
  if (raw === undefined || !/^\d+$/.test(raw)) {
    return null;
  }
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

const ConnectionCard: React.FunctionComponent<{ server: GpuServer; ip: string }> = ({ server, ip }) => (
  <Card>
    <CardTitle component="h2">Connection</CardTitle>
    <CardBody>
      <DescriptionList isHorizontal>
        <DescriptionListGroup>
          <DescriptionListTerm>IP address</DescriptionListTerm>
          <DescriptionListDescription>
            <ClipboardCopy
              isReadOnly
              hoverTip="Copy"
              clickTip="Copied"
              textAriaLabel={`IP address of ${server.hostname}`}
              copyAriaLabel={`Copy the IP address of ${server.hostname}`}
            >
              {ip}
            </ClipboardCopy>
          </DescriptionListDescription>
        </DescriptionListGroup>
        {GPU_SERVER_SSH_USER !== null && (
          <DescriptionListGroup>
            <DescriptionListTerm>SSH</DescriptionListTerm>
            <DescriptionListDescription>
              <ClipboardCopy
                isReadOnly
                isCode
                hoverTip="Copy"
                clickTip="Copied"
                textAriaLabel={`SSH command for ${server.hostname}`}
                copyAriaLabel={`Copy the SSH command for ${server.hostname}`}
              >
                {`ssh ${GPU_SERVER_SSH_USER}@${ip}`}
              </ClipboardCopy>
            </DescriptionListDescription>
          </DescriptionListGroup>
        )}
      </DescriptionList>
      <Content component="p" className="pf-v6-u-mt-md">
        Log in with one of the SSH keys you chose when starting it.
      </Content>
      <Content component="p">Shutting the server down does not stop billing — delete it to stop paying.</Content>
    </CardBody>
  </Card>
);

const BillingCard: React.FunctionComponent<{ server: GpuServer }> = ({ server }) => (
  <Card isFullHeight>
    <CardTitle component="h2">Billing</CardTitle>
    <CardBody>
      <DescriptionList isHorizontal>
        <DescriptionListGroup>
          <DescriptionListTerm>Price per hour</DescriptionListTerm>
          <DescriptionListDescription>
            {formatPrice(server.first_hour_ttc)} incl. VAT
            <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
              {formatUnitPrice(server.hourly_price_ht)} excl. VAT
            </div>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>VAT rate</DescriptionListTerm>
          <DescriptionListDescription>{trimDecimalZeros(server.vat_rate)} %</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Started</DescriptionListTerm>
          <DescriptionListDescription>{formatDateTime(server.started_at)}</DescriptionListDescription>
        </DescriptionListGroup>
        {/* A failed server's hour was refunded: its paid_through means nothing. */}
        {server.status !== 'failed' && (
          <DescriptionListGroup>
            <DescriptionListTerm>Paid until</DescriptionListTerm>
            <DescriptionListDescription>{formatDateTime(server.paid_through)}</DescriptionListDescription>
          </DescriptionListGroup>
        )}
      </DescriptionList>
      <Content component="p" className="pf-v6-u-mt-md">
        Each hour is paid in advance from your prepaid balance. If the balance cannot pay the next hour, the server is
        deleted with its data.
      </Content>
    </CardBody>
  </Card>
);

const ServerCard: React.FunctionComponent<{ server: GpuServer }> = ({ server }) => (
  <Card isFullHeight>
    <CardTitle component="h2">Server</CardTitle>
    <CardBody>
      <DescriptionList isHorizontal>
        <DescriptionListGroup>
          <DescriptionListTerm>Server</DescriptionListTerm>
          <DescriptionListDescription>{server.product?.label ?? '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Location</DescriptionListTerm>
          <DescriptionListDescription>{server.location_code}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>System image</DescriptionListTerm>
          <DescriptionListDescription>
            <span className="pf-v6-u-font-family-monospace">{server.image}</span>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>System disk</DescriptionListTerm>
          <DescriptionListDescription>{server.disk_gb} GB</DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
    </CardBody>
  </Card>
);

/**
 * Datacenter › GPU Servers › srv-… — one GPU server (docs/api-types/gpu-servers.md): its status in the
 * agreed customer copy, how to connect, what it costs, and Delete.
 *
 * A non-digit `:serverId` renders not-found without calling the API (the backend route is
 * `{id:\d+}`). The hook polls silently while the server is starting, being deleted or unpaid.
 */
function GpuServerDetail() {
  const auth = useAuth();
  const { serverId } = useParams<{ serverId: string }>();
  const id = parseGpuServerId(serverId);
  const { isImpersonating } = useImpersonation();
  const { server, loading, error, notFound, pollError, refetch, applyServer } = useGpuServer(id);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const loaded = !loading && error === null && !notFound && server !== null;
  const showDelete = loaded && server !== null && isDeletable(server.status);

  const renderStatus = (current: GpuServer) => {
    const copy = gpuServerStatusCopy(current);
    if (copy === null) {
      return null;
    }
    const disk = diskSentence(current);
    return (
      <Alert variant={STATUS_ALERT_VARIANT[current.status] ?? 'custom'} isInline title={copy} className="pf-v6-u-mb-md">
        {disk}
      </Alert>
    );
  };

  const renderContent = () => {
    if (loading && server === null && !notFound) {
      return <CenteredSpinner />;
    }

    if (notFound) {
      return (
        <EmptyState titleText="GPU server not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>This GPU server does not exist, or it belongs to another organisation.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/gpu-servers" />}>
                Back to GPU servers
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (error !== null || server === null) {
      return (
        <EmptyState
          titleText="Unable to load this GPU server"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error ?? 'An unexpected error occurred.'}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()} isLoading={loading} isDisabled={loading}>
                Retry
              </Button>
              <Button variant="link" component={(props) => <Link {...props} to="/datacenter/gpu-servers" />}>
                Back to GPU servers
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <GpuServerImpersonationAlert className="pf-v6-u-mb-md" />
        {pollError !== null && (
          <Alert
            variant="warning"
            isInline
            isPlain
            title="The status could not be refreshed — showing the last known state"
            className="pf-v6-u-mb-sm"
          >
            {pollError}
          </Alert>
        )}
        {renderStatus(server)}
        <Grid hasGutter>
          {server.ip !== null && (
            <GridItem span={12}>
              <ConnectionCard server={server} ip={server.ip} />
            </GridItem>
          )}
          <GridItem lg={6}>
            <BillingCard server={server} />
          </GridItem>
          <GridItem lg={6}>
            <ServerCard server={server} />
          </GridItem>
        </Grid>
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="GpuServerDetailBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/gpu-servers">GPU Servers</BreadcrumbItem>
          <BreadcrumbItem isActive>{server?.hostname ?? 'GPU server'}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <MicrochipIcon />
              </Icon>
              <span className="pf-v6-u-font-family-monospace">{server?.hostname ?? 'GPU server'}</span>
              {server !== null && (
                <span className="pf-v6-u-ml-sm">
                  <GpuServerStatusLabel status={server.status} />
                </span>
              )}
            </Content>
            <Content component="p">
              {server?.product?.label ?? 'A GPU server paid by the hour from your prepaid balance.'}
            </Content>
          </FlexItem>
          {!notFound && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                {showDelete && (
                  <FlexItem>
                    {isImpersonating ? (
                      <Tooltip content="Deleting a GPU server is not available while impersonating (staff: use the admin area).">
                        <Button variant="danger" icon={<TrashIcon />} isAriaDisabled>
                          Delete
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button variant="danger" icon={<TrashIcon />} onClick={() => setIsDeleteOpen(true)}>
                        Delete
                      </Button>
                    )}
                  </FlexItem>
                )}
                <FlexItem>
                  <Button
                    variant="secondary"
                    icon={<SyncAltIcon />}
                    isLoading={loading}
                    spinnerAriaValueText="Refreshing this GPU server"
                    onClick={() => {
                      if (!loading) {
                        void refetch();
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      {isDeleteOpen && server !== null && (
        <DeleteGpuServerModal server={server} onClose={() => setIsDeleteOpen(false)} onDeleted={applyServer} />
      )}
    </>
  );
}

export default GpuServerDetail;
